import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

import tableStyles from './table/tableStyles';
import DragHandleIcon from './table/DragHandleIcon';
import SortableHeaderCell from './table/SortableHeaderCell';
import DragOverlayHeader from './table/DragOverlayHeader';
import ColumnVisibilityToggle from './table/ColumnVisibilityToggle';

import { getRandomBorderColor } from "../utils/randomColor";

// Re-export for any consumers that import these directly from DraggableTable
export { tableStyles, DragHandleIcon, SortableHeaderCell, DragOverlayHeader, ColumnVisibilityToggle };

// ── Main DraggableTable ───────────────────────────────────────────────────────
/**
 * @param {Array}  columns      - [{ id, label, accessor, render? }]
 * @param {Array}  data         - row data
 * @param {string} emptyMessage
 */
export default function DraggableTable({ columns: initialColumns, data = [], emptyMessage = 'No data' }) {
  
 /*randome color*/
 const [color] = useState(getRandomBorderColor());

  const [columns, setColumns] = useState(initialColumns);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());
  const [activeId, setActiveId] = useState(null);

  // Keep columns in sync if parent passes new column definitions (e.g. after data loads)
  // We only sync ids/labels, preserving user's reorder
  React.useEffect(() => {
    setColumns((prev) => {
      const prevIds = prev.map((c) => c.id);
      const newIds = initialColumns.map((c) => c.id);
      // If column set changed, reset; otherwise update render fns in place
      if (JSON.stringify(prevIds) !== JSON.stringify(newIds)) return initialColumns;
      return prev.map((col) => {
        const updated = initialColumns.find((c) => c.id === col.id);
        return updated ? { ...col, render: updated.render } : col;
      });
    });
  }, [initialColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const visibleColumns = columns.filter((c) => !hiddenColumns.has(c.id));
  const activeColumn = columns.find((c) => c.id === activeId);

  const handleDragStart = ({ active }) => setActiveId(active.id);

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setColumns((prev) => {
      const oldIdx = prev.findIndex((c) => c.id === active.id);
      const newIdx = prev.findIndex((c) => c.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const handleDragCancel = () => setActiveId(null);

  const toggleColumn = (id) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
        <ColumnVisibilityToggle columns={columns} hiddenColumns={hiddenColumns} onToggle={toggleColumn} />
      </div>

      <div style={tableStyles.wrapper}>
      <table style={{ ...tableStyles.table, "--row-border": color }}>
          <thead>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={visibleColumns.map((c) => c.id)} strategy={horizontalListSortingStrategy}>
              <tr style={tableStyles.theadLeftBorder} className="border-l-[5px] border-[var(--row-border)]">
                  {visibleColumns.map((col) => (
                    <SortableHeaderCell key={col.id} column={col} />
                  ))}
                </tr>
              </SortableContext>

              <DragOverlay dropAnimation={{ duration: 180, easing: 'ease' }}>
                {activeColumn ? <DragOverlayHeader column={activeColumn} /> : null}
              </DragOverlay>
            </DndContext>
          </thead>

          <tbody>
            {data.length === 0 ? (
              <tr >
                <td colSpan={visibleColumns.length} style={tableStyles.emptyTd}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              data.map((row, rowIdx) => (
                <tr 
                 className="border-l-[5px] border-[var(--row-border)]"
                  key={row.id ?? rowIdx}
                  style={{
                    ...tableStyles.theadLeftBorder,
                    background: rowIdx % 2 === 0 ? 'var(--table-row-even)' : 'var(--table-row-odd)',
                    transition: 'background 150ms',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--table-hover)')}
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background =
                      rowIdx % 2 === 0 ? 'var(--table-row-even)' : 'var(--table-row-odd)')
                  }
                >
                  {visibleColumns.map((col) => (
                    <td key={col.id} style={tableStyles.tdBase}>
                      {col.render ? col.render(row) : (row[col.accessor] ?? '-')}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
